import PaginaNaoEncontrada from '@/pages/PaginaNaoEncontrada';
// A página inicial vem no pacote principal (24/10/2026): carregada à parte, ela atrasava o título — o maior conteúdo da
// tela — por uma ida e volta a mais (medido no Lighthouse do celular).
import LandingPage from "@/pages/LandingPage";
import { EsqueletoDeCartoes } from '@/components/common/Esqueleto';
import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import ProtectedRoute from "@/features/auth/components/ProtectedRoute";
import MainLayout from "@/components/layout/MainLayout";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { FullscreenOverlayProvider } from "@/features/teacher/layout/FullscreenOverlayContext";

// O Por Simulado aberto direto pede os dados junto com o código das rotas (`adiantarPorSimulado.ts`). Por `import()`: o
// módulo e o que ele lê ficam fora do pacote de entrada.
const adiantar = () => { void import("@/features/teacher/relatorios/adiantarPorSimulado").then((m) => m.adiantarPorSimulado()).catch(() => undefined); };
const QuizRoutes = lazy(() => import("@/routes/QuizRoutes"));
// O décimo sexto — era o único estático, e puxava o entry inteiro.
const GestaoRoutes = lazy(() => { adiantar(); return import("@/routes/GestaoRoutes"); });
const AdminRoutes = lazy(() => import("@/features/admin/routes/AdminRoutes"));
// A sala sem internet no celular SEM a sessão do portal (02/11/2026): a conta guardada no aparelho e o desbloqueio local
// bastam (`features/aplicativo/contaDaSala.ts`). A mesma página de `/teacher/sala-no-celular`.
const SalaNoCelularPage = lazy(() => import("@/features/salaNoCelular/SalaNoCelularPage"));
const TeacherRoutes = lazy(() => { adiantar(); return import("@/routes/TeacherRoutes"); });
const StudentRoutes = lazy(() => import("@/routes/StudentRoutes"));
const SimulationQuiz = lazy(() => import("@/features/quiz/components/SimulationQuiz").then(m => ({ default: m.SimulationQuiz })));
const PreVisualizacaoDaProva = lazy(() => import("@/features/quiz/pages/PreVisualizacaoDaProva").then(m => ({ default: m.PreVisualizacaoDaProva })));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const LayoutDoPapel = lazy(() => import("@/components/layout/LayoutDoPapel"));
const ContaPage = lazy(() => import("@/pages/ContaPage"));
const TestarAparelhoPage = lazy(() => import("@/pages/TestarAparelhoPage"));
const NotificacoesPage = lazy(() => import("@/features/notificacoes/NotificacoesPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const ExcluirContaPublicaPage = lazy(() => import("@/pages/ExcluirContaPublicaPage"));
// Pública de propósito: a landing anuncia as formas de acesso a quem ainda não
// entrou, e quem lê a promessa precisa poder cumpri-la sem conta (achado 139).
const ComoUsarPage = lazy(() => import("@/pages/ComoUsarPage"));
const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/features/auth/pages/RegisterPage"));
const SolicitarAcessoPage = lazy(() => import("@/features/pedidos/pages/SolicitarAcessoPage"));
const ConfirmarPedidoPage = lazy(() => import("@/features/pedidos/pages/ConfirmarPedidoPage"));
const TermosPage = lazy(() => import("@/features/termos/TermosPage"));
const AceitarTermosPage = lazy(() => import("@/features/termos/AceitarTermosPage"));
const ConfirmEmailPage = lazy(() => import("@/features/auth/pages/ConfirmEmailPage"));
const ForgotPasswordPage = lazy(() => import("@/features/auth/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/features/auth/pages/ResetPasswordPage"));
const TrocarSenhaObrigatoriaPage = lazy(() => import("@/features/auth/pages/TrocarSenhaObrigatoriaPage"));
const VincularGooglePage = lazy(() => import("@/features/auth/pages/VincularGooglePage"));
const DevCameraPage = lazy(() => import("@/pages/DevCameraPage"));

// A tela que ainda está chegando mostra a FORMA de uma lista, e não um indicador girando (item 9 da auditoria 3).
const PageFallback = () => (
  <div className="px-0 py-2 sm:p-6"><EsqueletoDeCartoes quantos={3} rotulo="Abrindo a tela…" /></div>
);

const AppRoutes = () => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const { pathname } = useLocation();

    // Mostra loading enquanto verifica autenticação
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-marca-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Carregando...</p>
                </div>
            </div>
        );
    }

    // Marcado para trocar a senha (achado 204): nenhuma outra tela antes da troca.
    if (user?.trocaDeSenhaObrigatoria && pathname !== "/trocar-senha") {
        return <Navigate to="/trocar-senha" replace />;
    }
    // Termos de uso pendentes (pedido de acesso): nenhuma tela antes do aceite — exceto as que
    // o próprio aceite cita (os termos públicos e a política).
    if (user?.termosPendentes && !["/termos/aceitar", "/termos", "/privacidade"].includes(pathname)) {
        return <Navigate to="/termos/aceitar" replace />;
    }

    return (
        <Suspense fallback={<PageFallback />}>
            <Routes>
                <Route element={<FullscreenOverlayProvider><MainLayout /></FullscreenOverlayProvider>}>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/sobre" element={<AboutPage />} />
                    <Route path="/privacidade" element={<PrivacyPage />} />
                    {/* Pública, sem login: a Play Store exige uma URL de exclusão de conta. */}
                    <Route path="/excluir-conta" element={<ExcluirContaPublicaPage />} />
                    <Route path="/como-usar" element={<ComoUsarPage />} />
                    {/*
                      O ENDEREÇO ANTIGO CONTINUA VALENDO, e não por gentileza:
                      `https://www.portalsimulados.com.br/sala-offline` está
                      escrito nas notas da release 1.0.2, que já foi publicada.
                      Nota de release publicada é texto que envelhece no lugar —
                      não dá para corrigi-la em quem já leu.

                      `replace` para não empilhar o endereço morto no histórico
                      do navegador, e a âncora para cair na seção certa.
                    */}
                    <Route path="/sala-offline" element={<Navigate to="/como-usar#sala-offline" replace />} />
                    <Route path="/dashboard" element={
                        isAuthenticated ? <DashboardPage /> : <Navigate to="/login" />
                    } />
                    {/* Minha conta — onde mora a exclusão exigida pela 5.1.1(v).
                        Vale para os quatro papéis, e por isso fica aqui e não
                        dentro de `/teacher`, `/admin`, `/gestao` ou `/student`:
                        uma tela em cada área seriam quatro telas a envelhecer. */}
                    {/* 1.4: o aluno confere se o aparelho serve para a prova "exigir" (`pages/TestarAparelhoPage.tsx`). */}
                    <Route path="/testar-aparelho" element={
                        isAuthenticated ? <TestarAparelhoPage /> : <Navigate to="/login" />
                    } />
                    {/* Minha conta e Notificações na moldura do papel (24/10/2026): o menu lateral da área no computador. */}
                    <Route element={<LayoutDoPapel />}>
                        <Route path="/conta" element={
                            isAuthenticated ? <ContaPage /> : <Navigate to="/login" />
                        } />
                        {/* A central de notificações (19/10/2026): também dos quatro papéis, pela mesma razão de `/conta`. */}
                        <Route path="/notificacoes" element={
                            isAuthenticated ? <NotificacoesPage /> : <Navigate to="/login" />
                        } />
                    </Route>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/sala-no-aparelho" element={<SalaNoCelularPage />} />
                    {/* Autocadastro de professor — rotas públicas, sem ProtectedRoute. */}
                    <Route path="/cadastro" element={<RegisterPage />} />
                    {/* Pedido de acesso de quem não é da rede — públicas. Ver pedidoDeAcessoService. */}
                    <Route path="/solicitar-acesso" element={<SolicitarAcessoPage />} />
                    <Route path="/solicitar-acesso/confirmar" element={<ConfirmarPedidoPage />} />
                    {/* Termos de uso: a página pública, e o aceite do primeiro acesso (pedido de acesso). */}
                    <Route path="/termos" element={<TermosPage />} />
                    <Route path="/termos/aceitar" element={
                        isAuthenticated ? <AceitarTermosPage /> : <Navigate to="/login" />
                    } />
                    <Route path="/confirmar-email" element={<ConfirmEmailPage />} />
                    {/* Recuperação de senha — rotas públicas. */}
                    <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
                    <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
                    {/* Achado 204. A guarda que vale é o 403 do servidor; esta é a saída dela. */}
                    <Route path="/trocar-senha" element={<TrocarSenhaObrigatoriaPage />} />
                    {/* A sugestão de vincular a conta Google, uma vez, depois do login do estudante. */}
                    <Route path="/vincular-google" element={<ProtectedRoute><VincularGooglePage /></ProtectedRoute>} />
                    <Route path="/dev/camera" element={<DevCameraPage />} />
                    <Route
                        path="/admin/*"
                        element={
                            <ProtectedRoute requiredRole="ADMIN">
                                <AdminRoutes />
                            </ProtectedRoute>
                        }
                    />
                    {/* Equipe gestora: navegação própria, sem o menu do professor. */}
                    <Route
                        path="/gestao/*"
                        element={
                            <ProtectedRoute requiredRole="SCHOOL_STAFF">
                                <GestaoRoutes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/teacher/*"
                        element={
                            <ProtectedRoute requiredRole="TEACHER">
                                <TeacherRoutes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/*"
                        element={
                            <ProtectedRoute requiredRole="STUDENT">
                                <StudentRoutes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/quiz/*"
                        element={
                            <ProtectedRoute requiredRole="STUDENT">
                                <QuizRoutes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/simulation/:id"
                        element={
                            <ProtectedRoute requiredRole="STUDENT">
                                <SimulationQuiz />
                            </ProtectedRoute>
                        }
                    />
                    {/* "Ver como o aluno" (25/10/2026): professor, gestão e administrador — a página recusa o aluno, e o
                        servidor também (`GET /api/simulations/:id/previa`). */}
                    <Route
                        path="/previa/simulado/:id"
                        element={
                            <ProtectedRoute>
                                <PreVisualizacaoDaProva />
                            </ProtectedRoute>
                        }
                    />
                    {/* Endereço que não existe: a tela "Página não encontrada", e não a tela branca (23/10/2026). */}
                    <Route path="*" element={<PaginaNaoEncontrada />} />
                </Route>
            </Routes>
        </Suspense>
    )
};

export default AppRoutes;